import { AlertState } from 'alerts/Alert'
import { NotFound } from 'fejl'
import { CancellationToken } from 'infra/cancellation'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import { computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import {
  asapScheduler,
  BehaviorSubject,
  defer,
  merge,
  MonoTypeOperatorFunction,
  NEVER,
  of,
  Subject,
  firstValueFrom,
} from 'rxjs'
import {
  distinctUntilChanged,
  endWith,
  filter,
  find,
  first,
  map,
  observeOn,
  share,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { Id } from 'types/definitions'
import { fromStream } from 'utils/mobx-util'
import { every, shareLatest, unpair, whileActive } from 'utils/rx-util'
import Job from '../Job'
import DeleteJobViewModel from '../views/DeleteJobDialog/DeleteJobViewModel'
import RenameJobViewModel from '../views/RenameJobDialog/RenameJobViewModel'
import { LegendSelected } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import IntakeQuestionsViewModel from './IntakeQuestionsViewModel'
import LegendSelectorViewModel from './LegendSelectorViewModel'
import { OutlineViewModel, StepContainer } from './OutlineViewModel'
import { Persister } from './Persister'
import { JobCreationPorts } from './ports'
import PreviousProviderSelectionViewModel from './PreviousProviderSelectionViewModel'
import SubmissionViewModel from './SubmissionViewModel'
import TeamSelectorViewModel from './TeamSelectorViewModel'
import WorkspaceSelectorViewModel from './WorkspaceSelectorViewModel'
import CopyJobViewModel from 'jobs/views/CopyJobDialog/CopyJobViewModel'
import NameInputViewModel from './NameViewModel'
import OwnerSelectorViewModel from './OwnerSelectorViewModel'
import RequestProviderSelectorViewModel from './RequestProviderSelectorViewModel'
import SupportingClientSelectorViewModel from './SupportingClientSelectorViewModel'

/**
 * Job Creation view model.
 *
 * Makes heavy use of Rx streams due to their automatic cleanup trait.
 * For example, the Job Rename and Delete dialogs will close when unsubscribed
 * from since if we navigate back and forth we don't want to keep the dialogs open.
 */
export default class JobCreationViewModel {
  /**
   * The preparation state.
   *
   * @private
   */
  private readonly _preparationState = new BehaviorSubject(
    PreparationState.needed(),
  )

  /**
   * Backing subject for VM active status.
   *
   * @private
   */
  private readonly _active = new BehaviorSubject(false)

  /**
   * Emits on dispose.
   *
   * @private
   */
  private readonly _disposed = new Subject()

  /**
   * Emit a value when the Job is deleted.
   *
   * @private
   */
  private readonly _jobDeleted = new Subject()

  /**
   * Emits when the editor is disposed.
   * Used as a stream terminator.
   */
  readonly disposed$ = this._disposed.pipe(
    shareLatest(),
    take(1),
    // Schedule emissions on the microtask queue.
    // Otherwise, if a subscriber calls `dispose`
    // from a subscription of a stream that uses
    // takeUntil(disposed$), the other subscribers will
    // not get notified.
    observeOn(asapScheduler),
  )

  /**
   * Whether the VM is active.
   * Completes on dispose.
   */
  readonly active$ = this._active.pipe(
    takeUntil(this.disposed$),
    endWith(false),
    distinctUntilChanged(),
  )

  /**
   * Persister. Maintains the Job instance.
   */
  readonly persister = this.createPersister()

  /**
   * Stream for the job being drafted once we have it.
   * Emits the current value on subscribe, or `Job.none` if there
   * is no draft job selected.
   */
  readonly job$ = this.persister.job$

  /**
   * Outline view model. Manages all the steps.
   */
  readonly outline = new OutlineViewModel(
    // Don't activate the outline until `prepare`
    // is done. That way the activation of the last pending step
    // is deferred until we have all the data to make that decision.
    this.active$.pipe(
      switchMap((isActive) =>
        isActive
          ? // Wait until we are prepared.
            this._preparationState.pipe(
              unpair(),
              find((s) => s === 'Prepared'),
              map(() => true),
            )
          : of(false),
      ),
    ),
    () => this.stepContainers,
  )

  /**
   * Name.
   */
  readonly nameInput = new NameInputViewModel(
    this.active$,
    this.outline.createStep,
    () => this.disabled,
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * Workspace selector.
   */
  readonly workspaceSelector: WorkspaceSelectorViewModel =
    new WorkspaceSelectorViewModel(
      this.active$,
      this.outline.createStep,
      this.adapter,
      () => this.jobWasCreated || this.disabled,
    )

  /**
   * Owner selector.
   */
  readonly ownerSelector = new OwnerSelectorViewModel(
    this.active$,
    this.outline.createStep,
    this.workspaceSelector.selectedWorkspaceMember$,
    this.adapter,
    () => this.jobWasCreated || this.disabled,
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * Team selector.
   */
  readonly teamSelector = new TeamSelectorViewModel(
    this.active$,
    this.outline.createStep,
    this.ownerSelector.selectedOwner$,
    this.adapter,
    () => this.jobWasCreated || this.disabled,
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * Supporting client selector.
   */
  readonly supportingClientSelector = new SupportingClientSelectorViewModel(
    this.active$,
    this.outline.createStep,
    this.workspaceSelector.selectedWorkspaceMember$,
    this.ownerSelector.selectedOwner$,
    this.adapter,
    () => this.disabled,
    () => this.jobWasCreated,
  )

  /**
   * Request provider selector.
   */
  readonly requestProviderSelector = new RequestProviderSelectorViewModel(
    this.active$,
    this.outline.createStep,
    this.workspaceSelector.selectedWorkspaceMember$,
    this.ownerSelector.selectedOwner$,
    this.adapter,
    () => this.disabled,
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * Legend selector.
   */
  readonly legendSelector = new LegendSelectorViewModel(
    this.active$,
    this.outline.createStep,
    this.workspaceSelector.selectedWorkspaceMember$,
    this.adapter,
    () => this.disabled,
  )

  /**
   * Intake questions.
   */
  readonly intakeQuestions = new IntakeQuestionsViewModel(
    this.active$,
    this.outline.createStep,
    this.adapter,
    () => this.disabled,
    this.job$,
    this.legendSelector.changes$
      .pipe(
        switchMap(() =>
          merge(
            of(null),
            this.persister.persistedChanges$.pipe(
              filter((c): c is LegendSelected => c.type === 'LegendSelected'),
              map((x) => x.legend),
            ),
          ),
        ),
      )
      .pipe(share()),
  )

  /**
   * Provider selection.
   */
  readonly previousProviderSelection = new PreviousProviderSelectionViewModel(
    this.active$,
    this.outline.createStep,
    this.adapter,
    this.workspaceSelector.selectedWorkspaceMember$,
    // Don't return the selected Legend until we have a Job instance.
    // This will prevent the step from showing up until then.
    this.job$.pipe(switchMap(() => this.legendSelector.selectedLegendVersion$)),
    () => this.disabled,
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * Submission.
   */
  readonly submission = new SubmissionViewModel(
    this.active$,
    this.outline.createStep,
    this.adapter,
    this.persister.queueChange,
    this.workspaceSelector.selectedWorkspaceMember$,
    this.teamSelector.selectedTeam$,
    this.job$,
    this.legendSelector.selectedLegendVersion$,
    every([
      this.ownerSelector.isValid$,
      this.workspaceSelector.selectedWorkspaceMember$,
      this.teamSelector.selectedTeam$,
      this.legendSelector.selectedLegendVersion$,
      this.intakeQuestions.allQuestionsCompleted$,
      this.previousProviderSelection.isValid$,
    ]),
    // Since we start the loading indicator on persist start, we also want to react to persist errors
    // to keep things running.
    this.persister.persist$.pipe(
      filter((r) => r.persistedChanges.length > 0 || !!r.errorMessage),
    ),
    this.persister.persisting$,
    () => this.submitRequest(),
    () => this.stepContainers.every((sc) => sc.step.valid),
    () => this.disabled,
    () => this.copyDraft(this.job),
    () => this.canEditDraftJobsAsAdmin,
  )

  /**
   * State slices.
   */
  readonly stateSlices: DraftStateSlice[] = [
    this.workspaceSelector,
    this.nameInput,
    this.ownerSelector,
    this.teamSelector,
    this.supportingClientSelector,
    this.requestProviderSelector,
    this.legendSelector,
    this.intakeQuestions,
    this.previousProviderSelection,
    this.submission,
  ]

  /**
   * Emit a value when the Draft was successfully submitted.
   */
  readonly jobSubmitted$ = this.submission.submitted$.pipe(
    switchMap(() => this.job$),
    this.whileActive(),
    share(),
  )

  /**
   * Emit a value when the Draft Job is created.
   */
  readonly jobCreated$ = this.persister.jobCreated$.pipe(
    this.whileActive(),
    takeUntil(this.disposed$),
    share(),
  )

  /**
   * Emit a value when we detect an attempt to load a non-draft Job.
   */
  readonly notADraft$ = this.persister.notADraft$.pipe(
    this.whileActive(),
    takeUntil(this.disposed$),
    share(),
  )

  /**
   * Emit a value when the Draft Job is deleted.
   */
  readonly jobDeleted$ = this._jobDeleted.pipe(
    this.whileActive(),
    takeUntil(this.disposed$),
    share(),
  )

  /**
   * The Rename modal.
   */
  readonly renameModal$ = this.job$.pipe(
    switchMap((job) =>
      new RenameJobViewModel(job, (_, newName) => this.renameDraft(newName))
        // Closes on unsubscribe.
        .asObservable(),
    ),
    this.whileActive(),
    share(),
  )

  /**
   * The Deleter.
   */
  readonly deleteModal$ = this.job$.pipe(
    switchMap((job) =>
      new DeleteJobViewModel(job, () => this.deleteDraft(job))
        // Closes on unsubscribe.
        .asObservable(),
    ),
    this.whileActive(),
    share(),
  )

  /**
   * The Copy modal.
   */
  readonly copyModal$ = this.job$.pipe(
    switchMap((job) =>
      new CopyJobViewModel(
        job,
        () => this.adapter.newAlert(),
        () => this.copyDraft(job),
      )
        // Closes on unsubscribe.
        .asObservable(),
    ),
    this.whileActive(),
    share(),
  )

  /**
   * Streams as MobX observables.
   * @private
   */
  private readonly $ = {
    renameModal: fromStream(RenameJobViewModel.none, this.renameModal$),
    deleteModal: fromStream(DeleteJobViewModel.none, this.deleteModal$),
    copyModal: fromStream(CopyJobViewModel.none, this.copyModal$),
    preparationState: fromStream(
      PreparationState.needed(),
      this._preparationState,
    ),
  }

  /**
   * Constructor.
   */
  constructor(
    protected adapter: JobCreationPorts,
    private _canEditDraftJobsAsAdmin: (workspaceId: Id) => boolean,
    private _disabled: () => boolean,
  ) {
    makeObservable(this)
    this.adapter.requestRename$
      .pipe(this.whileActive())
      .subscribe(() => this.renameModal.show())

    merge(this.jobSubmitted$, this.jobDeleted$).subscribe(() => this.dispose())
  }

  /**
   * The step containers.
   *
   * @private
   */
  @computed
  get stepContainers(): StepContainer[] {
    // Seeing as how this computed runs often,
    // using a plain for-in is preferable.
    const stepContainers: StepContainer[] = []
    let breaker = false
    for (let i = 0; i < this.stateSlices.length; i++) {
      const slice = this.stateSlices[i]
      for (let j = 0; j < slice.stepContainers.length; j++) {
        const stepContainer = slice.stepContainers[j]
        stepContainers.push(stepContainer)
        // If the step causes a short-circuit (Breaker), stop collecting.
        if (stepContainer.step.isShortCircuited) {
          breaker = true
          break
        }
      }

      if (breaker) {
        break
      }
    }

    return stepContainers
  }

  /**
   * Job delete modal.
   */
  @computed
  get deleteModal() {
    return this.$.deleteModal()
  }

  /**
   * Job rename modal.
   */
  @computed
  get renameModal() {
    return this.$.renameModal()
  }

  /**
   * Job copy modal.
   */
  @computed
  get copyModal() {
    return this.$.copyModal()
  }

  /**
   * The Job that is being drafted.
   */
  @computed
  get job() {
    return this.persister.job
  }

  /**
   * Whether the editor is completely disabled.
   */
  @computed
  get disabled(): boolean {
    return this._disabled() || this.submission.submit.pending
  }

  /**
   * Whether the job has been created or if we can still
   * make foundational changes to it.
   */
  @computed
  get jobWasCreated() {
    return this.persister.jobWasCreated
  }

  /**
   * View title.
   */
  @computed
  get viewTitle() {
    const [state] = this.$.preparationState()
    if (state !== 'Prepared') {
      return 'Loading...'
    }

    return this.job === Job.none ? 'New Job' : this.job.displayName
  }

  /**
   * Whether the editor is for admins.
   */
  @computed
  get canEditDraftJobsAsAdmin(): boolean {
    const workspaceId = this.workspaceSelector.selectedWorkspace?.id
    if (!Id.some(workspaceId)) {
      return false
    }

    return this._canEditDraftJobsAsAdmin(workspaceId)
  }

  /**
   * When the parent controller is trying to load an instance of
   * the editor, if it is asked to load an editor for a draft that is
   * already set as the current editor, it may skip the preparation
   * step if the editor is revivable. If the editor is revivable,
   * you should call `revive`.
   *
   * The editor will be revivable when:
   *   - We are trying to edit an EXISTING draft (the ID is non-empty)
   *   - AND that ID represents the draft of this editor
   *   - AND the job is still a draft
   *   - AND preparation is not needed
   *
   * @param jobId
   */
  isRevivable(jobId: Id) {
    if (!Id.some(jobId) || this.job.id !== jobId) {
      return false
    }

    if (this.job.status !== 'UNDER_CONSTRUCTION') {
      return false
    }

    const [state] = this.$.preparationState()
    return state !== 'Needed'
  }

  /**
   * Activates the VM.
   */
  activate = () => this._active.next(true)

  /**
   * Deactivates the VM.
   */
  deactivate = () => this._active.next(false)

  /**
   * Prepares the View Model with an existing draft, or fetching data
   * to prepare for a new draft.
   *
   * Will be called when:
   *   1. Loading an existing draft
   *   2. Starting a new draft
   *
   * Will NOT be called after a server draft instance is created
   * in point 2 above, because at that point we already have that
   * draft's data loaded.
   *
   * @param jobId If specified, loads the Job
   * @param newDraft If creating a new draft, uses this prefilled input
   * @param ct The cancellation token
   */
  prepare = task(
    async (jobId: Id, newDraft: PrefilledDraftInput, ct: CancellationToken) => {
      try {
        this._preparationState.next(PreparationState.needed())
        await this.prepareImpl(jobId, newDraft, ct)
        ct.throwIfCancelled()
        this._preparationState.next(PreparationState.prepared())
      } catch (error) {
        this._preparationState.next(PreparationState.needed(error))
        throw error
      }
    },
  )

  /**
   * Revives the editor.
   * DO NOT call this unless `isRevivable` returns `true`!
   *
   * If `prepare` is currently running, wait for it to complete.
   * If preparation failed, throws that error.
   */
  async revive() {
    this.activate()
    const [, error] = await firstValueFrom(
      this._preparationState.pipe(first(([s]) => s !== 'Running')),
    )

    if (error) {
      throw error
    }

    this.stateSlices.forEach((slice) => slice.ready())
  }

  /**
   * Disposes the VM, stops all running stuff.
   */
  dispose() {
    this._disposed.next(true)
  }

  /**
   * Creates the Persister. Only called once.
   *
   * @private
   */
  private createPersister(): Persister {
    // This circumvents the circular dependency that the state slices
    // have on the Persister (the `job$` observable).
    // Basically, we defer creating the changes observable until the first
    // time the VM becomes active.
    // We also only want to persist changes while the VM is active.
    const changes$ = defer(() =>
      merge(...this.stateSlices.map((sc) => sc.changes$ || NEVER)),
    ).pipe(this.whileActive())

    return new Persister(changes$, this.adapter)
  }

  /**
   * Queues a Rename change and waits for it to complete.
   *
   * @private
   */
  private async renameDraft(newName: string) {
    await this.persister.queueChange({
      type: 'Renamed',
      newName,
    })
    this.adapter
      .newAlert()
      .withState(AlertState.Success)
      .saying('Success!', `Draft renamed to "${this.job.displayName}"`)
      .autoDismiss()
      .show()
  }

  /**
   * Deletes the draft, then navigates to Jobs.
   * @param job
   */
  private async deleteDraft(job: Job) {
    await this.adapter.deleteDraft(job)
    this._jobDeleted.next(undefined)
  }

  /**
   * Copies the draft
   * @param job
   */
  private async copyDraft(job: Job) {
    return this.adapter.copyDraft(job)
  }

  /**
   * Shortcut for the whileActive operator using the active$ stream.
   *
   * @private
   */
  private whileActive<T>(): MonoTypeOperatorFunction<T> {
    return whileActive(this.active$)
  }

  /**
   * Prepare implementation.
   *
   * @param jobId
   * @param newDraft
   * @param ct
   * @private
   */
  private async prepareImpl(
    jobId: Id,
    newDraft: PrefilledDraftInput,
    ct: CancellationToken,
  ) {
    // NOTE: Activation and deactivation on the editor are idempotent.
    // If it has already been done, it's fine.
    this.activate()

    // Wait for any pending flushes in case some were buffered.
    await this.persister.waitForFlush(ct)
    ct.throwIfCancelled()

    // The Persister is responsible for the Job instance,
    // so tell it to fetch the Job (if there is one).
    try {
      await this.persister.prepare(jobId, ct)
    } catch (err) {
      // If we didn't find the job, tell the world that it was deleted.
      if (err instanceof NotFound) {
        this._jobDeleted.next(undefined)
      }
      throw err
    }

    ct.throwIfCancelled()

    // The view passes in a `newDraft` context which will be used
    // for new drafts if no existing Job was loaded.
    const context: PrepareContext =
      this.job === Job.none
        ? { type: 'NewDraft', prefilledDraft: newDraft }
        : { type: 'EditDraft', job: this.job }

    // The Job (if any) has been loaded, time to prepare the
    // state slices.
    await Promise.all(
      this.stateSlices.map(async (slice) => {
        await slice.prepare(context, ct)
        slice.ready()
      }),
    )

    // Starts persisting; any buffered changes will be persisted.
    this.persister.start()

    // If we are creating a new Draft and have enough info to persist it now,
    // wait for that to happen. In case we don't, the wait will resolve
    // immediately. We also wait for the first set of questions to load.
    // NOTE: Both of these complete on cancel.
    // We wait for them in sequence because after the Persister has flushed...
    await this.persister.waitForFlush(ct)
    // ... the Intake might have a Job instance which means it started querying.
    await this.intakeQuestions.waitForNextQuery(ct)
  }

  /**
   * Submit the request.
   *
   * @private
   */
  private async submitRequest() {
    await this.persister.waitForFlush()

    // When the current actor has the ability to bypass billing,
    // the job's owner default payment method will be used.
    const paymentMethod = this.submission.billing.canBypassBilling
      ? null
      : this.submission.billing.selectedPaymentMethod

    await this.adapter.submitRequest(
      this.job,
      paymentMethod,
      this.submission.billing.bypassBilling,
    )
  }
}

/**
 * Describes the preparation state for the VM.
 */
export type PreparationState = ['Needed', Error?] | ['Running'] | ['Prepared']
export const PreparationState = {
  /**
   * Preparation is needed; you should call `prepare` with
   * a new cancellation token. It could either be that the previous
   * Prepare failed/was cancelled or that it hasn't been run yet.
   * If it has a truthy 2nd element, it is an error.
   */
  needed(error?: Error): PreparationState {
    return ['Needed', error]
  },
  /**
   * Prepare is currently running; you can wait for it to finish.
   */
  running(): PreparationState {
    return ['Running']
  },
  /**
   * The VM has been prepared at least once; you MAY run it again with a new
   * cancellation token.
   */
  prepared(): PreparationState {
    return ['Prepared']
  },
}
